import React, { useState, useEffect } from 'react';
import './Heatmap.css';
import { API_BASE_URL as API_BASE } from '../config';

const Heatmap = ({ onBack }) => {
  // Use a valid backend key with Nifty 50 as default sector and 1D as default timeframe
  const [selectedSector, setSelectedSector] = useState('Nifty 50');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1D');
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Must match backend sector_stocks keys exactly
  const sectors = [
    'Nifty 50',
    'Sensex',
    'Nifty IT',
    'Pharma & Healthcare',
    'FMCG',
    'Auto',
    'Oil & Gas',
    'Infra',
    'Energy',
    'Media',
    'Metal',
    'Nifty Private Bank',
    'Nifty PSU Bank',
    'Realty',
    'Nifty Commodities'
  ];

  const timeframes = ['1D', '1W', '1M', '3M', '6M', '1Y'];

  // Sync dark mode with root html tag class
  useEffect(() => {
    const darkMode = document.documentElement.classList.contains('darkmode');
    setIsDarkMode(darkMode);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          const isNowDark = document.documentElement.classList.contains('darkmode');
          setIsDarkMode(isNowDark);
        }
      }
    });

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Fetch data when sector or timeframe changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // Build URL with query parameters
        const url = new URL(`${API_BASE}/api/sector-abnormal-growth`);
        url.searchParams.append('sector', selectedSector);
        url.searchParams.append('timeframe', selectedTimeframe);

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          // Try to get error message from response
          let errorMessage = `HTTP ${response.status}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (e) {
            // If we can't parse the error as JSON, use the status text
            errorMessage = response.statusText || errorMessage;
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();

        // Process the response data
        const processed = [];
        const seenSymbols = new Set(); // Track symbols to avoid duplicates
        
        // Check if data is nested under 'data' property
        const responseData = result.data || result;
        
        // Process ranked stocks if available (include 0.00% changes as well)
        if (responseData && Array.isArray(responseData.ranked_stocks)) {
          responseData.ranked_stocks.forEach((stock, idx) => {
            if (stock && stock.symbol && !seenSymbols.has(stock.symbol)) {
              const rawChange = stock.avg_growth;
              const parsed = rawChange !== null && rawChange !== undefined
                ? parseFloat(rawChange)
                : 0;
              const change = Number.isFinite(parsed) ? parsed : 0;

              seenSymbols.add(stock.symbol);
              processed.push({
                symbol: stock.symbol,
                name: stock.company_name || stock.symbol,
                change,
                size: idx < 5 ? 'large' : idx < 10 ? 'medium' : 'small'
              });
            }
          });
        }

        // Process abnormal growth stocks if available (also keep 0.00%)
        if (responseData && Array.isArray(responseData.abnormal_growth_stocks)) {
          responseData.abnormal_growth_stocks.forEach(stock => {
            if (stock && stock.symbol && !seenSymbols.has(stock.symbol)) {
              const rawChange = stock.avg_growth;
              const parsed = rawChange !== null && rawChange !== undefined
                ? parseFloat(rawChange)
                : 0;
              const change = Number.isFinite(parsed) ? parsed : 0;

              seenSymbols.add(stock.symbol);
              processed.push({
                symbol: stock.symbol,
                name: stock.company_name || stock.symbol,
                change,
                size: 'large',
                isAbnormal: true
              });
            }
          });
        }

        // If no data was processed, show a message
        if (processed.length === 0) {
          console.log('No data processed. Raw response:', result);
          setError('No data available for the selected sector and timeframe');
        } else {
          // Sort tiles by absolute percentage change (largest magnitude first)
          // so strong negative and positive movers are interleaved naturally
          const sortedByChange = [...processed].sort(
            (a, b) => Math.abs(b.change) - Math.abs(a.change)
          );
          setHeatmapData(sortedByChange);
        }
      } catch (err) {
        console.error('Error fetching heatmap data:', err);
        setError(`Failed to load heatmap data: ${err.message || 'Unknown error occurred'}`);
        setHeatmapData([]); // Clear any previous data on error
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to avoid rapid API calls when changing filters
    const timer = setTimeout(() => {
      fetchData().catch(console.error);
    }, 300);

    // Cleanup function to cancel any pending requests
    return () => clearTimeout(timer);
  }, [selectedSector, selectedTimeframe]);

  // Color and intensity mapping from avg_growth
  const getIntensityClass = (change) => {
    const abs = Math.abs(change);
    if (abs >= 3) return 'intense';
    if (abs >= 2) return 'strong';
    if (abs >= 1) return 'moderate';
    return 'light';
  };

  const getColorClass = (change) => (change >= 0 ? 'positive' : 'negative');

  const rootClasses = `heatmap-page ${isDarkMode ? 'darkmode' : ''}`;

  return (
    <div className={rootClasses}>
      <header className="heatmap-header">
        <div className="heatmap-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
          <div className="heatmap-user">
            <button className="heatmap-back-btn" onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      </header>

      <main className="heatmap-main">
        <div className="heatmap-container">
          <div className="heatmap-title">
            <h1>Sector Heatmap</h1>
            <div className="heatmap-info">
              <span className="info-icon">ℹ️</span>
              <span className="info-text">Visual market performance analysis</span>
            </div>
          </div>

          {/* Controls */}
          <div className="heatmap-controls">
            <div className="sector-selector">
              <label>Sector:</label>
              <select
                value={selectedSector}
                onChange={(e) => setSelectedSector(e.target.value)}
                className="sector-select"
              >
                {sectors.map((sector) => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>

            <div className="timeframe-selector">
              <label>Timeframe:</label>
              <div className="timeframe-buttons">
                {timeframes.map((timeframe) => (
                  <button
                    key={timeframe}
                    className={`timeframe-btn ${selectedTimeframe === timeframe ? 'active' : ''}`}
                    onClick={() => setSelectedTimeframe(timeframe)}
                  >
                    {timeframe}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Heatmap Grid */}
          <div className="heatmap-grid-container">
            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p>{error}</p>
            ) : (
              <div className="heatmap-grid">
                {heatmapData.map((stock, index) => (
                  <div
                    key={index}
                    className={`heatmap-tile ${stock.size} ${getColorClass(stock.change)} ${getIntensityClass(stock.change)}`}
                    title={`${stock.name}: ${stock.change >= 0 ? '+' : ''}${stock.change.toFixed(2)}%`}
                  >
                    <div className="tile-content">
                      <div className="tile-symbol">{stock.symbol}</div>
                      <div className="tile-change">
                        {stock.change >= 0 ? '+' : ''}{stock.change.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Heatmap;